'use client';

import { getIsActiveLink } from '.';
import Link from 'next/link';
import { ComponentType, RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ServerMenu } from '../helpers';
import {
  MenuLinkClientWrapper,
  MenuLinkClientWrapperProps,
} from './MenuLinkClientWrapper';
import { getSportsHref } from '@/helpers/variables';
import { useDevice } from '@/hooks/device';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import theme from '@/theme';

export const SportLinkLoader = ({
  name,
  locale,
}: {
  name: string;
  locale: string;
}) => {
  const { isMobileDevice } = useDevice();
  const IconMobile = theme.tabBarMenu.sportsMobileMenuIcon.component;
  const linkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (typeof window === 'undefined') {
      return null;
    }
    const sportLink = getSportsHref(window.location.hostname, locale);
    window.location.replace(sportLink);
  };
  return (
    <li>
      <a onClick={linkClick}>
        {isMobileDevice ? <IconMobile /> : null}
        {name}
      </a>
    </li>
  );
};

export type SportsClientLinkProps = {
  id: ServerMenu;
  icon?: ComponentType;
  iconMobile?: ComponentType;
  ariaLabel?: string;
  target?: string;
  isMobileDevice?: boolean;
  locale: string;
  activeItemRef: RefObject<HTMLLIElement>;
  currentPage: string;
  handleClickMenuItem: () => void;
} & Omit<MenuLinkClientWrapperProps, 'url'>;

const SportsClientLink = ({
  id,
  icon: Icon,
  ariaLabel,
  target,
  locale,
  activeItemRef,
  currentPage,
  hideConditions,
  hideConditionResults,
  handleClickMenuItem,
}: SportsClientLinkProps) => {
  const { t } = useTranslation();
  const { hostname } = useServer();
  const { isMobileDevice } = useDevice();
  const IconMobile = theme.tabBarMenu.sportsMobileMenuIcon.component;

  const name = t('menu.sports');

  const arrowElement = useMemo(() => {
    if (!isMobileDevice) return null;

    return (
      <div className="arrow">
        <theme.images.arrowUpGrayIcon.component />
      </div>
    );
  }, [isMobileDevice]);

  const url = getSportsHref(hostname, locale);

  return (
    <MenuLinkClientWrapper
      id={id}
      url={url}
      ref={getIsActiveLink(url, currentPage, activeItemRef)}
      hideConditions={hideConditions}
      hideConditionResults={hideConditionResults}
      onClick={handleClickMenuItem}
    >
      <Link
        href={getSportsHref(hostname, locale)}
        target={target}
        className={locale?.toLowerCase()}
        aria-label={ariaLabel || name}
      >
        {isMobileDevice ? IconMobile && <IconMobile /> : Icon && <Icon />}
        {name}
      </Link>
      {arrowElement}
    </MenuLinkClientWrapper>
  );
};

export default SportsClientLink;
